import { ExternalLink, GitFork, Quote, Star } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'
import { FaGithub, FaLinkedin } from 'react-icons/fa'

import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

import { HallOfFameProject } from '@/types/hall-of-fame-project.type'

interface HallOfFameProjectCardProps {
  project: HallOfFameProject
  className?: string
}

/**
 * Renders metric badges for 'project' type (stars/forks) or custom
 * metric1/metric2 labels for other entry types.
 */
function ProjectMetrics({ project }: { project: HallOfFameProject }) {
  const type = project.type || 'project'

  // Project type with GitHub data → backward-compatible stars/forks display
  if (type === 'project' && (project.stars || project.forks)) {
    return (
      <div className="flex items-center gap-4 flex-wrap">
        {project.stars > 0 && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
            <Star className="h-4 w-4 fill-current" />
            <span className="font-medium text-xs">{project.stars}</span>
          </div>
        )}
        {project.forks > 0 && (
          <div className="flex items-center gap-2 text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border">
            <GitFork className="h-4 w-4" />
            <span className="font-medium text-xs">{project.forks}</span>
          </div>
        )}
      </div>
    )
  }

  // Other types → custom metric labels
  if (!project.metric1Label && !project.metric2Label) return null

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {project.metric1Label && (
        <div className="flex items-center gap-2 text-primary bg-primary/5 px-3 py-1.5 rounded-full border border-primary/20">
          <span className="font-medium text-xs">
            {project.metric1Label}: {project.metric1Value}
          </span>
        </div>
      )}
      {project.metric2Label && (
        <div className="flex items-center gap-2 text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border">
          <span className="font-medium text-xs">
            {project.metric2Label}: {project.metric2Value}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Renders social links in the card footer.
 * Shows icons conditionally based on which URLs are populated.
 */
function SocialLinks({ project }: { project: HallOfFameProject }) {
  const hasGithub = !!project.githubUrl
  const hasLinkedin = project.linkedinUrl && project.linkedinUrl.includes('linkedin.com')
  const hasExternal = !!project.externalUrl

  if (!hasGithub && !hasLinkedin && !hasExternal) return null

  return (
    <div className="flex items-center gap-1">
      {hasLinkedin && (
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          asChild
        >
          <Link href={project.linkedinUrl} target="_blank" rel="noopener noreferrer">
            <FaLinkedin className="size-4" />
            <span className="sr-only">LinkedIn Profile</span>
          </Link>
        </Button>
      )}
      {hasGithub && (
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          asChild
        >
          <Link href={project.githubUrl} target="_blank" rel="noopener noreferrer">
            <FaGithub className="size-4" />
            <span className="sr-only">GitHub Repository</span>
          </Link>
        </Button>
      )}
      {hasExternal && (
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
          asChild
        >
          {/* eslint-disable-next-line */}
          <Link href={project.externalUrl!} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="size-4" />
            <span className="sr-only">External Link</span>
          </Link>
        </Button>
      )}
    </div>
  )
}

/**
 * Universal Hall of Fame card that adapts its layout based on entry `type`:
 * - project: GitHub-style with stars/forks (backward compatible with FSDS)
 * - certification: Badge-focused with description
 * - achievement: Custom metrics with milestone display
 * - testimonial: Quote-style layout with large avatar
 */
function HallOfFameProjectCard({ project, className }: HallOfFameProjectCardProps) {
  const { text } = useLocale()
  const type = project.type || 'project'
  const primaryUrl = project.externalUrl || project.githubUrl || '#'
  const isTestimonial = type === 'testimonial'

  // Testimonial variant — quote-style card
  if (isTestimonial) {
    return (
      <Card
        key={project.id}
        className={cn(
          'group flex flex-col overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 border bg-card',
          className
        )}
      >
        <CardContent className="flex-1 p-6 space-y-4">
          {/* Quote */}
          <div className="flex gap-3">
            <Quote className="h-6 w-6 text-primary/30 flex-shrink-0 mt-1" />
            <p className="text-sm text-card-foreground leading-relaxed italic line-clamp-4">
              {project.description || project.title}
            </p>
          </div>

          {/* Badge */}
          {project.badgeLabel && (
            <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-primary/5 to-primary/10 text-primary border border-primary/20 font-medium px-3 py-1 rounded-full text-xs">
              {project.badgeIcon && <span>{project.badgeIcon}</span>}
              {project.badgeLabel}
            </div>
          )}
        </CardContent>

        <CardFooter className="px-6 py-4 bg-muted/50 border-t border-border">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                <AvatarImage src={project.userAuthor?.avatar || '/images/avatar.png'} />
                <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs font-semibold">
                  {project.userAuthor
                    ? `${project.userAuthor.firstName?.[0] || ''}${project.userAuthor.lastName?.[0] || ''}`
                    : project.author?.[0] || '👤'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                {project.userAuthor ? (
                  <Link
                    href={`/user/${project.userAuthor.profileId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200 hover:underline"
                  >
                    {project.userAuthor.firstName} {project.userAuthor.lastName}
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-foreground">{project.author}</span>
                )}
                {project.title && project.description && (
                  <span className="text-xs text-muted-foreground line-clamp-1">{project.title}</span>
                )}
              </div>
            </div>
            <SocialLinks project={project} />
          </div>
        </CardFooter>
      </Card>
    )
  }

  // Default variant — works for project, certification, achievement
  return (
    <Card
      key={project.id}
      className={cn(
        'group flex flex-col overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 border bg-card',
        className
      )}
    >
      {/* Project Image with Overlay */}
      <div className="relative">
        <Link target="_blank" rel="noopener noreferrer" href={primaryUrl} className="block">
          <CardHeader className="p-0 relative">
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10">
              <Image
                src={project.imageUrl || '/placeholder.svg'}
                alt={project.title}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 100vw"
                className="object-cover transition-all duration-500"
                fill
                priority
                quality={100}
                loading="eager"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* View Details button overlay */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <Button size="sm" className="bg-card/90 text-primary hover:bg-card shadow-lg backdrop-blur-sm">
                  <ExternalLink className="size-3 mr-1" />
                  {text.HALL_OF_FAME.VIEW_DETAILS}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Link>
      </div>

      {/* Content */}
      <CardContent className="flex-1 p-6 space-y-3">
        {/* Badge */}
        {project.badgeLabel && (
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-primary/5 to-primary/10 text-primary border border-primary/20 font-medium px-3 py-1 rounded-full text-xs mb-2">
            {project.badgeIcon && <span>{project.badgeIcon}</span>}
            {project.badgeLabel}
          </div>
        )}

        {/* Title */}
        <Link target="_blank" rel="noopener noreferrer" href={primaryUrl}>
          <CardTitle className="line-clamp-2 text-xl font-bold text-card-foreground group-hover:text-primary transition-colors duration-200 leading-tight">
            {project.title}
          </CardTitle>
        </Link>

        {/* Description */}
        {project.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
        )}

        {/* Metrics */}
        <ProjectMetrics project={project} />
      </CardContent>

      {/* Footer */}
      <CardFooter className="px-6 py-4 bg-muted/50 border-t border-border">
        <div className="flex w-full items-center justify-between">
          {/* Author Info */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-8 w-8 border-2 border-white shadow-sm">
                <ResolvedAvatarImage avatar={project.userAuthor?.avatar} alt={project.userAuthor?.firstName || 'Author'} />
                <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs font-semibold">
                  {project.userAuthor
                    ? `${project.userAuthor.firstName?.[0] || ''}${project.userAuthor.lastName?.[0] || ''}`
                    : project.author?.[0] || '👤'}
                </AvatarFallback>
              </Avatar>
            </div>
            {project.userAuthor ? (
              <Link
                href={`/user/${project.userAuthor.profileId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-foreground hover:text-primary transition-colors duration-200 hover:underline"
              >
                {project.userAuthor.firstName} {project.userAuthor.lastName}
              </Link>
            ) : (
              <span className="text-sm font-medium text-foreground">{project.author}</span>
            )}
          </div>

          {/* Social Links */}
          <SocialLinks project={project} />
        </div>
      </CardFooter>
    </Card>
  )
}

export default HallOfFameProjectCard
